import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PACKAGE_STATUS_APPROVED } from '~/consts/package';
import OffersService from '~/services/OffersService';
import PackagesService from '~/services/PackagesService';
import ReservationService from '~/services/ReservationService';

import { getHotelUniquePropertyIds } from '~/utils/hotelUtils';

import { offerQueryKeys } from './useGetOffer';

export function usePublishRentalOffer(offerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      const offer = (await OffersService.getOffer(offerId))?.result;
      if (!offer) {
        throw new Error('Failed to load offer to publish');
      }
      const propertyIds = getHotelUniquePropertyIds(offer);

      if (propertyIds.length === 0) {
        throw new Error('Offer has no linked property to publish');
      }

      await Promise.all(propertyIds.map((propertyId) => ReservationService.publishRentalProperty(propertyId)));

      await Promise.all(
        offer.packages
          .filter((pkg) => pkg.status !== PACKAGE_STATUS_APPROVED)
          .map((pkg) => PackagesService.updatePackageStatus(PACKAGE_STATUS_APPROVED, offerId, pkg.le_package_id)),
      );

      return OffersService.updateOffer({ status: PACKAGE_STATUS_APPROVED }, offerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
    },
  });
}
